import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const CIRCUMRADIUS_VALUES = [4, 5, 6, 7, 8, 10, 12, 15];
const INRADIUS_VALUES = [2, 3, 4, 5, 6, 7, 8, 10];
const SCENARIOS = [
  { wording: "triangleInCircle", circleType: "circumcircle" },
  { wording: "circleAroundTriangle", circleType: "circumcircle" },
  { wording: "circleInTriangle", circleType: "incircle" },
  { wording: "triangleAroundCircle", circleType: "incircle" },
];
const pick = (items) => items[Math.floor(Math.random() * items.length)];

export function generateTask(options = {}) {
  const original = options.variant === "original" && options.session === "2026_MAJ";
  const scenario = original ? SCENARIOS[0] : pick(SCENARIOS);
  const n = original ? 9 : pick(scenario.circleType === "circumcircle" ? CIRCUMRADIUS_VALUES : INRADIUS_VALUES);
  const answer = scenario.circleType === "circumcircle" ? 3 * n : 6 * n;
  return {
    type: "MaturaEquilateralTriangleCircumradiusOpenTask",
    data: {
      n,
      answer,
      wording: scenario.wording,
      circleType: scenario.circleType,
      radiusSymbol: scenario.circleType === "circumcircle" ? "R" : "r",
      maxPoints: 1,
    },
    source: {
      variant: original ? "original" : "random",
      exam: "MATURA",
      session: original ? "2026_MAJ" : undefined,
      taskNumber: "22",
    },
  };
}

const numberValue = (value) => Number(String(value?.parsed ?? value?.raw ?? value ?? "").replace(",", "."));

function taskSentence(data) {
  const radius = <InlineMath math={`${data.n}\\sqrt{3}`} />;
  if (data.wording === "circleAroundTriangle") return <>Na trójkącie równobocznym opisano okrąg o promieniu {radius}.</>;
  if (data.wording === "circleInTriangle") return <>W trójkąt równoboczny wpisano okrąg o promieniu {radius}.</>;
  if (data.wording === "triangleAroundCircle") return <>Na okręgu o promieniu {radius} opisano trójkąt równoboczny.</>;
  return <>W okrąg o promieniu {radius} wpisano trójkąt równoboczny.</>;
}

function deductionSentence(data) {
  if (data.wording === "circleAroundTriangle") {
    return "Okrąg opisano na trójkącie, więc podany promień jest promieniem okręgu opisanego.";
  }
  if (data.wording === "circleInTriangle") {
    return "Okrąg wpisano w trójkąt, więc podany promień jest promieniem okręgu wpisanego.";
  }
  if (data.wording === "triangleAroundCircle") {
    return "Trójkąt opisano na okręgu, zatem okrąg jest wpisany w trójkąt.";
  }
  return "Trójkąt wpisano w okrąg, zatem okrąg jest opisany na trójkącie.";
}

export function renderTask({ data }) {
  return <p>{taskSentence(data)} Oblicz długość boku tego trójkąta.</p>;
}

export function renderInput(_task, value = {}, onChange, disabled) {
  return <div style={{ display: "grid", gap: 8, textAlign: "left" }}>
    <div><InlineMath math="a=" /></div>
    <FractionInput value={value?.raw ?? value ?? ""} onChange={onChange} disabled={disabled} label={"a"} />
  </div>;
}

export function checkAnswer({ data }, value = {}) {
  return Math.abs(numberValue(value) - data.answer) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`a=${data.answer}`} />;
}

export function renderUserAnswer(value = {}) {
  return <InlineMath math={`a=${String(value?.raw ?? value ?? "-")}`} />;
}

export function renderExplanation({ data }) {
  const isCircumcircle = data.circleType === "circumcircle";
  return <div>
    <p><strong>Krok 1. Dedukcja.</strong> {deductionSentence(data)}</p>
    <p><strong>Krok 2.</strong> Korzystamy ze wzoru na wysokość trójkąta równobocznego:</p>
    <BlockMath math={String.raw`h=\frac{a\sqrt{3}}{2}`} />
    <p><strong>Krok 3.</strong> Łączymy wysokość z promieniem {isCircumcircle ? "okręgu opisanego" : "okręgu wpisanego"}.</p>
    {isCircumcircle ? <>
      <BlockMath math={String.raw`R=\frac23h,\qquad h=\frac32R`} />
      <BlockMath math={`h=\\frac32\\cdot ${data.n}\\sqrt{3}=\\frac{${3 * data.n}\\sqrt{3}}{2}`} />
    </> : <>
      <BlockMath math={String.raw`r=\frac13h,\qquad h=3r`} />
      <BlockMath math={`h=3\\cdot ${data.n}\\sqrt{3}=${3 * data.n}\\sqrt{3}`} />
    </>}
    <p><strong>Krok 4.</strong> Porównujemy dwa zapisy wysokości.</p>
    {isCircumcircle
      ? <BlockMath math={`\\frac{a\\sqrt{3}}{2}=\\frac{${3 * data.n}\\sqrt{3}}{2},\\qquad a=${data.answer}`} />
      : <BlockMath math={`\\frac{a\\sqrt{3}}{2}=${3 * data.n}\\sqrt{3},\\qquad a=${data.answer}`} />}
  </div>;
}
